import { useCallback, useMemo, useState } from "react";

import useQuery from "../search/useQuery";
import QUERY_TAGS from "../search/queryTags";
import { invalidateTags } from "../search/queryCache";
import {
  createTransferRecipient,
  deleteTransferRecipient,
  fetchTransferRecipients,
  setDefaultTransferRecipient,
} from "./financeService";
import { mapTransferRecipients } from "./financeMappers";
import { pickList } from "./financePage";
import { DETAIL_STALE_TIME } from "./financeCache";

const useTransferRecipients = ({ enabled = true } = {}) => {
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const query = useQuery({
    key: enabled ? "finance:transfer-recipients" : null,
    tag: QUERY_TAGS.TRANSFER_RECIPIENTS,
    staleTime: DETAIL_STALE_TIME,
    enabled,
    fetcher: fetchTransferRecipients,
  });

  const recipients = useMemo(
    () => mapTransferRecipients(pickList(query.currentData, "transferRecipients", "recipients")),
    [query.currentData]
  );

  const reload = useCallback(async () => {
    invalidateTags([QUERY_TAGS.TRANSFER_RECIPIENTS]);
    await query.refetch();
  }, [query]);

  const create = useCallback(
    async (payload) => {
      setSaving(true);

      try {
        await createTransferRecipient(payload);
        await reload();

        return { status: "saved" };
      } catch (error) {
        return { status: "error", error };
      } finally {
        setSaving(false);
      }
    },
    [reload]
  );

  const remove = useCallback(
    async (id) => {
      setRemovingId(id);

      try {
        await deleteTransferRecipient(id);
        await reload();

        return { status: "removed" };
      } catch (error) {
        return { status: "error", error };
      } finally {
        setRemovingId(null);
      }
    },
    [reload]
  );

  const makeDefault = useCallback(
    async (id) => {
      try {
        await setDefaultTransferRecipient(id);
        await reload();

        return { status: "saved" };
      } catch (error) {
        return { status: "error", error };
      }
    },
    [reload]
  );

  return {
    recipients,
    isLoading: query.currentData === undefined && !query.isError && query.enabled,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    saving,
    removingId,
    create,
    remove,
    makeDefault,
    refetch: query.refetch,
  };
};

export default useTransferRecipients;
